// =============================================================================
// src/views/workspace/index.ts — markup + barrel da rota Workspace (funcional).
//
// Shell ESTÁTICO do Workspace Pessoal. Os cards e o feed de atividade são
//   preenchidos em runtime a partir do store (workspace-section.ts); aqui ficam
//   apenas os pontos de montagem estáveis + busca + chips de ordenação + modal de
//   "Nova análise" + botão "Restaurar exemplos". init(ctx) liga a rota injetando o
//   activator da seção (mesmo padrão do Grafo).
//
// DESVIO DE PARIDADE consciente (aprovado): esta tela deixa de espelhar unified.html
//   para virar feature real. Reusa as classes existentes (u-ws-card/u-chip/u-cta-btn)
//   para preservar o visual; estilos novos vivem em src/styles/inline-overrides.css.
// =============================================================================

import type { AppContext, AppModule, ZonaKey } from '../../app/types';
import type { WorkspaceTag } from './workspace-types';
import { MANAUS_ZONA_DISPLAY_NAME } from '../../data/manaus-zones';
import { WORKSPACE_SECTION_OPTIONS } from './workspace-sections';
import { WorkspaceRoute } from './workspace-route';
import { activateWorkspaceSection } from './workspace-section';

/** As 5 tags oferecidas como checkbox no modal de criação. */
const WORKSPACE_TAG_OPTIONS: readonly WorkspaceTag[] = [
  'eleição', 'zona norte', '2024', 'urgente', 'rascunho',
];

/** <option> de cada zona (+ "Nenhuma") para o select de região do modal. */
function zonaOptionsMarkup(): string {
  const keys = Object.keys(MANAUS_ZONA_DISPLAY_NAME) as ZonaKey[];
  const opts = keys.map((key) => `<option value="${key}">${MANAUS_ZONA_DISPLAY_NAME[key]}</option>`);
  return `<option value="">Nenhuma (só a seção)</option>${opts.join('')}`;
}

/** <option> de cada seção-destino navegável. */
function sectionOptionsMarkup(): string {
  return WORKSPACE_SECTION_OPTIONS.map(
    (opt) => `<option value="${opt.id}">${opt.label}</option>`,
  ).join('');
}

/** Checkboxes de tags do modal. */
function tagCheckboxesMarkup(): string {
  return WORKSPACE_TAG_OPTIONS.map(
    (tag) =>
      `<label class="u-ws-modal-tag"><input type="checkbox" name="tags" value="${tag}"> ${tag}</label>`,
  ).join('');
}

/** Um item da navegação lateral com badge de contagem. */
function navItemMarkup(view: string, icon: string, label: string, active: boolean): string {
  const cls = active ? 'u-ws-nav-item active' : 'u-ws-nav-item';
  const base = 'padding:6px 8px;border-radius:6px;cursor:pointer;font-size:12px;display:flex;align-items:center;gap:6px';
  const tone = active ? 'background:#E6F1FB;color:#0C447C;font-weight:500' : 'color:var(--color-text-secondary)';
  return (
    `<div class="${cls}" data-view="${view}" data-label="${label}" style="${base};${tone}">` +
    `<i class="ti ${icon}" style="font-size:13px"></i> ${label}` +
    `<span class="u-ws-nav-count" id="u-ws-count-${view}" style="margin-left:auto;font-size:10px;color:var(--color-text-tertiary)"></span>` +
    '</div>'
  );
}

/** Uma pill de tag da sidebar (filtro single-select). */
function sidebarTagMarkup(tag: WorkspaceTag, bg: string, fg: string): string {
  return `<span class="u-ws-tag" data-tag="${tag}" style="font-size:10px;padding:2px 7px;border-radius:999px;background:${bg};color:${fg};cursor:pointer">${tag}</span>`;
}

/** Sidebar: criar, navegação, tags e restaurar exemplos. */
const SIDEBAR_MARKUP =
  '<div class="u-workspace-page-side">' +
  '<button class="u-cta-btn" id="u-ws-new-btn" style="width:100%"><i class="ti ti-plus"></i> Nova análise</button>' +
  '<div><div class="u-ws-side-label">Navegação</div><div style="display:flex;flex-direction:column;gap:1px">' +
  navItemMarkup('mine', 'ti-folder', 'Minhas análises', true) +
  navItemMarkup('shared', 'ti-share', 'Compartilhadas', false) +
  navItemMarkup('team', 'ti-users', 'Do time', false) +
  navItemMarkup('recent', 'ti-clock', 'Recentes', false) +
  navItemMarkup('favorites', 'ti-star', 'Favoritos', false) +
  '</div></div>' +
  '<div><div class="u-ws-side-label">Tags</div><div style="display:flex;flex-wrap:wrap;gap:3px">' +
  sidebarTagMarkup('eleição', '#E6F1FB', '#0C447C') +
  sidebarTagMarkup('zona norte', '#EAF3DE', '#3B6D11') +
  sidebarTagMarkup('2024', '#FAEEDA', '#854F0B') +
  sidebarTagMarkup('urgente', '#FCEBEB', '#A32D2D') +
  sidebarTagMarkup('rascunho', 'var(--color-background-secondary)', 'var(--color-text-secondary)') +
  '</div></div>' +
  '<button class="u-ws-reset" id="u-ws-reset-btn" type="button"><i class="ti ti-refresh"></i> Restaurar exemplos</button>' +
  '</div>';

/** Toolbar do grid: título, busca, chips de ordenação e toggle grid/list. */
const TOOLBAR_MARKUP =
  '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap">' +
  '<h3 id="u-ws-title" style="margin:0;font-size:15px;font-weight:600;color:var(--color-text-primary)">Minhas análises</h3>' +
  '<div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">' +
  '<input id="u-ws-search" class="u-ws-search" type="search" placeholder="Buscar análises…" aria-label="Buscar análises">' +
  '<span class="u-chip selected" data-sort="recent">Recentes</span>' +
  '<span class="u-chip" data-sort="alpha">A–Z</span>' +
  '<span class="u-chip" data-sort="views">Mais vistas</span>' +
  '<span class="u-ws-view-toggle" style="display:flex;background:var(--color-background-secondary);padding:2px;border-radius:var(--border-radius-md);gap:2px">' +
  '<button class="active" data-view="grid" style="border:none;background:var(--color-background-primary);border-radius:4px;padding:4px 7px;cursor:pointer;font-size:10px;font-family:inherit"><i class="ti ti-layout-grid"></i></button>' +
  '<button data-view="list" style="border:none;background:transparent;border-radius:4px;padding:4px 7px;cursor:pointer;font-size:10px;font-family:inherit;color:var(--color-text-secondary)"><i class="ti ti-list"></i></button>' +
  '</span></div></div>';

/** Painel de detalhe (alvos atualizados por applyDetailPanel). */
const DETAIL_MARKUP =
  '<div class="u-workspace-page-detail">' +
  '<div style="height:100px;background:var(--color-background-tertiary);border-radius:var(--border-radius-md);display:flex;align-items:center;justify-content:center;border:0.5px solid var(--color-border-tertiary)"><i class="ti ti-map" style="font-size:28px;color:var(--color-text-tertiary)"></i></div>' +
  '<div><div id="u-ws-detail-title" style="font-size:13px;font-weight:500;color:var(--color-text-primary);margin-bottom:4px"></div>' +
  '<div id="u-ws-detail-share" style="font-size:10px;color:var(--color-text-secondary);display:flex;align-items:center;gap:5px"></div></div>' +
  '<div style="display:flex;flex-direction:column;gap:4px;font-size:11px">' +
  '<div style="display:flex;justify-content:space-between;padding:2px 0"><span style="color:var(--color-text-secondary)">Criado em</span><span id="u-ws-created"></span></div>' +
  '<div style="display:flex;justify-content:space-between;padding:2px 0"><span style="color:var(--color-text-secondary)">Camadas</span><span id="u-ws-layers"></span></div>' +
  '<div style="display:flex;justify-content:space-between;padding:2px 0"><span style="color:var(--color-text-secondary)">Vistas</span><span id="u-ws-views"></span></div></div>' +
  '<div><div class="u-ws-side-label">Atividade recente</div><div id="u-ws-activity" style="display:flex;flex-direction:column;gap:6px"></div></div>' +
  '<button class="u-cta-btn secondary" id="u-ws-open-btn" style="width:100%"><i class="ti ti-external-link"></i> Abrir análise</button>' +
  '</div>';

/** Modal "Nova análise" (pré-preenchido pela captura seção+zona no runtime). */
const CREATE_MODAL_MARKUP =
  '<div id="u-ws-create-modal" class="u-ws-modal hidden" aria-hidden="true">' +
  '<form id="u-ws-create-form" class="u-ws-modal-card">' +
  '<div class="u-ws-modal-title">Nova análise</div>' +
  '<label class="u-ws-modal-field">Título<input name="title" required maxlength="80" placeholder="Ex.: Zona Norte — cenário"></label>' +
  `<div class="u-ws-modal-field">Tags<div class="u-ws-modal-tags">${tagCheckboxesMarkup()}</div></div>` +
  `<label class="u-ws-modal-field">Região<select name="zona">${zonaOptionsMarkup()}</select></label>` +
  `<label class="u-ws-modal-field">Seção-destino<select name="targetSection">${sectionOptionsMarkup()}</select></label>` +
  '<label class="u-ws-modal-field">Ano (opcional)<input name="year" maxlength="12" placeholder="Ex.: 2024"></label>' +
  '<label class="u-ws-modal-field">Camada (opcional)<input name="layerLabel" maxlength="40" placeholder="Ex.: 3 ativas"></label>' +
  '<div class="u-ws-modal-actions">' +
  '<button type="button" id="u-ws-create-cancel" class="u-cta-btn secondary">Cancelar</button>' +
  '<button type="submit" class="u-cta-btn">Criar</button>' +
  '</div></form></div>';

/** Markup completo da rota Workspace (shell estático). */
const MARKUP =
  '<section class="u-workspace-page" id="u-workspace-page" aria-label="Workspace Pessoal" aria-hidden="true">' +
  '<div class="u-workspace-page-header">' +
  '<button type="button" class="u-workspace-page-back" id="u-workspace-page-back" aria-label="Voltar ao mapa"><i class="ti ti-arrow-left"></i><span>Voltar ao mapa</span></button>' +
  '<div class="u-workspace-page-heading"><div class="u-workspace-page-title">Workspace Pessoal</div><div class="u-workspace-page-sub">Gestão de análises, relatórios e colaboração em equipe</div></div>' +
  '</div>' +
  '<div class="u-workspace-page-body">' +
  SIDEBAR_MARKUP +
  '<div class="u-workspace-page-main">' +
  TOOLBAR_MARKUP +
  '<div id="u-ws-card-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:10px"></div>' +
  '</div>' +
  DETAIL_MARKUP +
  '</div>' +
  CREATE_MODAL_MARKUP +
  '</section>';

/** Devolve a string HTML literal deste bloco (superfície consumida por main.ts). */
export function buildWorkspacePageMarkup(): string {
  return MARKUP;
}

/**
 * AppModule da rota Workspace. render() devolve o shell; init(ctx) liga a rota
 * injetando o AppContext + o activator da seção (sem import estático de
 * workspace-section na rota — mesmo desenho do Grafo).
 */
export const workspaceView: AppModule = {
  id: 'workspace',
  render(): string {
    return MARKUP;
  },
  init(ctx: AppContext): void {
    WorkspaceRoute.init(ctx, activateWorkspaceSection);
  },
};

export { WorkspaceRoute } from './workspace-route';
export { activateWorkspaceSection } from './workspace-section';
export { applyDetailPanel } from './workspace-detail-render';
export default workspaceView;
